import { Actions, Effect, ofType } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { map, switchMap, tap, filter, debounceTime } from 'rxjs/operators';
import { defer, of } from 'rxjs';
import { LocaleService } from '@app/core/i18n';
import { DataLayerService } from '../services/data-layer.service';
import { ProductActionTypes } from '@app/+product/store';
import { CartActionTypes } from '@app/core/cart/actions/cart.actions';
import { CookieService } from '@app/core/cookies';
import {
  mapUser,
  mapProduct,
  mapListProducts,
  mapCatalogPageUrl,
  mapProductPageUrl,
  USER_INITIAL_STATE,
  PRODUCT_INITIAL_STATE,
  mapCatalogDataLayer
} from '@app/analytics/models/mapping';
import { User } from '@vestiaire/api-sdk-angular';

import * as DataLayerActions from '../actions/data-layer';
import * as UserActions from '@app/core/user/store/actions/user.actions';
import * as CatalogActions from '@app/+catalog/store/actions/catalog.action';
import * as ProductActions from '@app/+product/store/actions/product.action';
import * as CartActions from '@app/core/cart/actions/cart.actions';

@Injectable()
export class DataLayerEffects {
  @Effect()
  getUserSuccess$ = this.actions$.pipe(
    ofType(UserActions.AUTHENTICATED_SUCCESS),
    map((action: UserActions.AuthenticatedSuccess) => action.payload),
    filter(payload => payload.authenticated),
    switchMap(({ user }) => {
      const state = {
        authAttempted: true,
        ...mapUser(user),
        ISO_country_code: this.lookupCountry(user)
      };

      return of(new DataLayerActions.Persist(state));
    })
  );

  @Effect()
  getUserFailure$ = this.actions$.pipe(
    ofType(UserActions.AUTHENTICATED_FAILURE),
    map((action: UserActions.AuthenticatedFailure) => action.payload),
    switchMap(() =>
      of(
        new DataLayerActions.Persist({
          authAttempted: true
        })
      )
    )
  );

  @Effect()
  loginSuccess$ = this.actions$.pipe(
    ofType(UserActions.LOGIN_SUCCESS),
    map((action: UserActions.LoginSuccess) => action.payload.session.user),
    switchMap(user => {
      const state = {
        ...mapUser(user),
        ISO_country_code: this.lookupCountry(user)
      };
      return of(new DataLayerActions.Persist(state));
    })
  );

  @Effect()
  logoutSuccess$ = this.actions$.pipe(
    ofType(UserActions.LOGOUT_SUCCESS),
    switchMap(action => {
      const resetState = {
        ...USER_INITIAL_STATE,
        ISO_country_code: this.lookupCountry()
      };
      return of(new DataLayerActions.Persist(resetState));
    })
  );

  @Effect()
  persist$ = this.actions$.pipe(
    ofType(DataLayerActions.DataLayerActionTypes.Persist),
    debounceTime(500), // We don't need to flush every time we persist
    switchMap(action => of(new DataLayerActions.Flush()))
  );

  @Effect({ dispatch: false })
  flush$ = this.actions$.pipe(
    ofType(DataLayerActions.DataLayerActionTypes.Flush),
    tap(action => {
      this.dataLayerService.flush();
    })
  );

  @Effect()
  registerNewsletter$ = this.actions$.pipe(
    ofType(UserActions.OPEN_REGISTER_POPIN),
    switchMap(action => {
      return of(new DataLayerActions.Persist(null));
    })
  );

  @Effect()
  registerForm$ = this.actions$.pipe(
    ofType(UserActions.OPEN_REGISTER_FORM),
    switchMap(action => {
      return of(new DataLayerActions.Persist(null));
    })
  );

  @Effect()
  OnCatalogSearch = this.actions$.pipe(
    ofType(CatalogActions.CATALOG_PAGE_SEARCH),
    map((action: CatalogActions.OnCatalogPageSearch) => action.payload),
    switchMap(data => {
      const state = {
        ...mapListProducts(data['products']),
        ...mapCatalogDataLayer(data)
      };
      return of(new DataLayerActions.Persist(state));
    })
  );

  @Effect()
  OnCatalogInit = this.actions$.pipe(
    ofType(CatalogActions.CATALOG_PAGE_LOAD),
    map((action: CatalogActions.OnCatalogPageLoad) => action.payload),
    switchMap(page => {
      return of(new DataLayerActions.Persist(mapCatalogPageUrl(page)));
    })
  );

  @Effect()
  loadCartSuccess$ = this.actions$.pipe(
    ofType(CartActionTypes.LoadCartSuccess),
    map((action: CartActions.LoadCartSuccess) => action.payload.order),
    switchMap(order => {
      const state = { order_id: '' };

      if (order) {
        state['order_id'] = String(order.id);
      }
      return of(new DataLayerActions.Persist(state));
    })
  );

  @Effect()
  loadCartFailure$ = this.actions$.pipe(
    ofType(CartActionTypes.LoadCartFailure),
    switchMap(action => {
      return of(new DataLayerActions.Persist({ order_id: '' }));
    })
  );

  @Effect()
  loadProductSuccess$ = this.actions$.pipe(
    ofType(ProductActionTypes.LoadProductSuccess),
    map((action: ProductActions.LoadProductSuccess) => action.payload),
    switchMap(product => {
      const state = {
        ...mapProduct(product),
        ...mapProductPageUrl(product)
      };
      return of(new DataLayerActions.Persist(state));
    })
  );

  @Effect()
  unloadProduct$ = this.actions$.pipe(
    ofType(ProductActionTypes.UnloadProduct),
    map((action: ProductActions.UnloadProduct) => action.payload),
    switchMap(product => {
      return of(new DataLayerActions.Persist(PRODUCT_INITIAL_STATE));
    })
  );

  @Effect()
  $init = defer(() => {
    return of(
      new DataLayerActions.Persist({
        id_site: String(this.localeService.getRegion()),
        env_country: this.localeService.getRegionCode(),
        env_language: this.localeService.getTranslationKey(),
        order_currency: this.localeService.getCurrency(),
        ISO_country_code: this.lookupCountry()
      })
    );
  });

  private ipCountryCode = '';

  constructor(
    private actions$: Actions,
    private dataLayerService: DataLayerService,
    private localeService: LocaleService,
    private cookieService: CookieService
  ) {
    const countryCookie = this.cookieService.get('vc_country');

    if (countryCookie) {
      this.ipCountryCode = String(countryCookie);
    }
  }

  public lookupCountry(user?: User) {
    if (user && user.address) {
      return user.address.country.isoCode;
    }

    return this.ipCountryCode;
  }
}
